import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { AXON_TOOLBOX_URL } from "../constants";
import { writeToClipboard } from "../hooks/useClipboard";
import { Dialog } from "./Dialog";

interface Props {
  open: boolean;
  onClose: () => void;
}

type TargetId = "linkedin" | "whatsapp" | "email";
type BannerKind = "success" | "error";

interface Banner {
  kind: BannerKind;
  message: string;
}

const TARGET_ICONS: Record<TargetId, string> = {
  linkedin: "share",
  whatsapp: "chat",
  email: "mail",
};

function buildTargetUrl(target: TargetId, text: string, subject: string): string {
  const encodedText = encodeURIComponent(text);
  const encodedUrl = encodeURIComponent(AXON_TOOLBOX_URL);
  switch (target) {
    case "linkedin":
      return `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
    case "whatsapp":
      return `https://wa.me/?text=${encodedText}`;
    case "email":
      return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodedText}`;
  }
}

export function ShareDialog({ open, onClose }: Props) {
  const { t } = useTranslation(["share", "common"]);

  const defaultMessage = useMemo(
    () =>
      `${t("share:casual", {
        tool: "Axon Toolbox",
        axon: "Axon Group",
      })}\n\n${t("share:footnote", { url: AXON_TOOLBOX_URL })}`,
    [t],
  );

  const [text, setText] = useState(defaultMessage);
  const [banner, setBanner] = useState<Banner | null>(null);
  const [busyTarget, setBusyTarget] = useState<TargetId | "copy" | null>(null);

  useEffect(() => {
    if (open) {
      setText(defaultMessage);
      setBanner(null);
      setBusyTarget(null);
    }
  }, [open, defaultMessage]);

  const handleCopy = async () => {
    setBusyTarget("copy");
    const ok = await writeToClipboard(text);
    setBanner({
      kind: ok ? "success" : "error",
      message: ok ? t("share:feedback.copied") : t("share:feedback.copyFailed"),
    });
    setBusyTarget(null);
  };

  const handleTarget = async (target: TargetId) => {
    setBusyTarget(target);
    try {
      if (target === "linkedin") {
        const copied = await writeToClipboard(text);
        setBanner({
          kind: copied ? "success" : "error",
          message: copied
            ? t("share:feedback.linkedinClipboard")
            : t("share:feedback.copyFailed"),
        });
      }
      const url = buildTargetUrl(target, text, t("share:emailSubject"));
      window.open(url, "_blank", "noopener,noreferrer");
    } finally {
      setBusyTarget(null);
    }
  };

  const bannerClasses =
    banner?.kind === "success"
      ? "border-signal-success/40 bg-signal-success/10 text-signal-success"
      : "border-signal-error/40 bg-signal-error/10 text-signal-error";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      icon="share"
      title={t("share:title")}
      width="rich"
      footer={
        <>
          <button
            type="button"
            onClick={handleCopy}
            disabled={busyTarget === "copy"}
            className="h-footer-btn px-3 inline-flex items-center gap-1 rounded-sm text-12 font-bold uppercase tracking-widerx text-brand-primary bg-brand-primary/10 hover:bg-brand-primary/20 transition-colors duration-150 disabled:cursor-wait disabled:opacity-60 focus-visible:outline-none focus-visible:border-brand-primary/60 focus-visible:shadow-[0_0_0_2px_rgb(var(--brand-primary)/0.4)] border border-transparent"
          >
            <span className="material-symbols-outlined text-[14px]">
              {busyTarget === "copy" ? "check" : "content_copy"}
            </span>
            {busyTarget === "copy" ? t("share:copying") : t("share:copy")}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="h-footer-btn px-3 inline-flex items-center rounded-sm text-12 font-bold uppercase tracking-widerx text-text-muted hover:text-text-main hover:bg-surface-subtle transition-colors duration-150 focus-visible:outline-none focus-visible:border-brand-primary/60 focus-visible:shadow-[0_0_0_2px_rgb(var(--brand-primary)/0.4)] border border-transparent"
          >
            {t("share:close")}
          </button>
        </>
      }
    >
      <p className="m-0 text-12 leading-[17px] text-text-muted">{t("share:description")}</p>

      <label className="flex flex-col gap-1">
        <span className="text-12 font-bold uppercase tracking-widerx text-text-muted">
          {t("share:textLabel")}
        </span>
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          rows={6}
          className="w-full p-2 bg-surface-subtle/40 border border-border-subtle rounded-sm text-13 leading-[19px] text-text-main font-sans resize-y outline-none transition-colors duration-150 focus:bg-surface-subtle/60 focus:border-brand-primary/60 focus:shadow-[0_0_0_2px_rgb(var(--brand-primary)/0.4)]"
        />
      </label>

      <div className="flex flex-col gap-2">
        <span className="text-12 font-bold uppercase tracking-widerx text-text-muted">
          {t("share:targetsLabel")}
        </span>
        <div className="flex flex-wrap gap-2">
          {(["linkedin", "whatsapp", "email"] as TargetId[]).map(target => {
            const busy = busyTarget === target;
            return (
              <button
                key={target}
                type="button"
                onClick={() => void handleTarget(target)}
                disabled={busy}
                className="h-footer-btn px-3 inline-flex items-center gap-1 rounded-sm text-12 font-bold uppercase tracking-widerx text-brand-primary bg-brand-primary/10 hover:bg-brand-primary/20 transition-colors duration-150 disabled:cursor-wait disabled:opacity-60 focus-visible:outline-none focus-visible:border-brand-primary/60 focus-visible:shadow-[0_0_0_2px_rgb(var(--brand-primary)/0.4)] border border-transparent"
              >
                <span className="material-symbols-outlined text-[14px]">
                  {TARGET_ICONS[target]}
                </span>
                {busy ? t("share:opening") : t(`share:target.${target}`)}
              </button>
            );
          })}
        </div>
        <p className="m-0 text-11 leading-4 text-text-muted/70">{t("share:linkedinNote")}</p>
      </div>

      {banner && (
        <div
          role="status"
          className={`text-12 leading-[17px] border rounded-sm px-2 py-1.5 ${bannerClasses}`}
        >
          {banner.message}
        </div>
      )}
    </Dialog>
  );
}
